import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { logAuthEvent } from '../../lib/authLogger';
import AuthLayout from './AuthLayout';
import { validateEmail, sanitizeHTML } from '../../lib/validation';

const Signup = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const validatePassword = (pwd) => {
    // Basic stronger policy (optional): 8+ chars, at least one letter & number
    if (pwd.length < 8) return 'Password must be at least 8 characters.';
    if (!/[A-Za-z]/.test(pwd) || !/\d/.test(pwd)) return 'Password must include letters and numbers.';
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    // Validate and sanitize email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error);
      return;
    }

    const sanitizedEmail = emailValidation.sanitized;

    // Check domain restriction
    const domainAllowed = sanitizedEmail.endsWith('@lexaeon.com');
    if (!domainAllowed) {
      const msg = 'Only @lexaeon.com email addresses are allowed to sign up.';
      setError(msg);
      logAuthEvent({ type: 'signup', email: sanitizedEmail, success: false, message: msg });
      return;
    }

    // Validate password match
    if (password !== confirm) {
      const msg = 'Passwords do not match';
      setError(msg);
      logAuthEvent({ type: 'signup', email: sanitizedEmail, success: false, message: msg });
      return;
    }

    // Validate password strength
    const pwdErr = validatePassword(password);
    if (pwdErr) {
      setError(pwdErr);
      logAuthEvent({ type: 'signup', email: sanitizedEmail, success: false, message: pwdErr });
      return;
    }

    setLoading(true);
    try {
      // Attempt signup directly - Supabase will handle duplicate detection
      const { data, error: authError } = await supabase.auth.signUp({ 
        email: sanitizedEmail, 
        password 
      });
      
      if (authError) {
        const sanitizedError = sanitizeHTML(authError.message);

        // Check if error indicates user already exists
        if (authError.message.includes('already registered') ||
            authError.message.includes('already exists') ||
            authError.message.includes('User already registered') ||
            authError.message.includes('already been registered') ||
            authError.message.includes('email address is already registered') ||
            authError.message.includes('A user with this email address has already been registered') ||
            authError.code === 'user_already_exists' ||
            authError.code === 'email_address_taken') {
          const msg = 'An account with this email address already exists. Please try signing in instead.';
          setError(msg);
          logAuthEvent({ type: 'signup', email: sanitizedEmail, success: false, message: msg });
          return;
        }

        setError(sanitizedError);
        logAuthEvent({ type: 'signup', email: sanitizedEmail, success: false, message: sanitizedError, meta: { code: authError.code } });
        return;
      }

      // Check if user was created but already exists (Supabase sometimes returns success but no session for existing users)
      if (data?.user && !data?.session && data?.user?.email_confirmed_at) {
        const msg = 'An account with this email address already exists. Please try signing in instead.';
        setError(msg);
        logAuthEvent({ type: 'signup', email: sanitizedEmail, success: false, message: msg });
        return;
      }
      
      // If email confirmation disabled & session returned, could immediately redirect
      if (data?.session) {
        logAuthEvent({ type: 'signup', email: sanitizedEmail, success: true, message: 'Signup complete; session established.' });
        navigate('/file-naming', { replace: true });
        return;
      }
      
      logAuthEvent({ type: 'signup', email: sanitizedEmail, success: true, message: 'Signup initiated; confirmation email sent.' });
      setMessage('Check your email to confirm your account.');
      timeoutRef.current = setTimeout(() => navigate('/login'), 4000);
    } catch (err) {
      // Additional catch for network errors or unexpected issues
      let msg = sanitizeHTML(err?.message || 'Unexpected error during signup');

      // Check if the caught error also indicates user exists
      if (msg.includes('already registered') ||
          msg.includes('already exists') ||
          msg.includes('User already registered') ||
          msg.includes('email address is already registered')) {
        msg = 'An account with this email address already exists. Please try signing in instead.';
      }

      setError(msg);
      logAuthEvent({ type: 'signup', email: sanitizedEmail, success: false, message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Create Account" subtitle="Sign up to get started">
      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-gray-500">(only @lexaeon.com addresses)</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
            placeholder="yourname@lexaeon.com"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
            placeholder="••••••••"
            autoComplete="new-password"
            minLength={6}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
            placeholder="••••••••"
            autoComplete="new-password"
            minLength={6}
          />
        </div>
  {error && <div role="alert" aria-live="polite" className="text-sm text-red-600 font-medium bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
  {message && <div role="status" aria-live="polite" className="text-sm text-green-700 font-medium bg-green-50 border border-green-200 rounded-lg px-3 py-2">{message}</div>}
        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex justify-center items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold shadow hover:from-blue-700 hover:to-blue-800 disabled:opacity-60"
        >
          {loading ? 'Creating...' : 'Sign Up'}
        </button>
        <div className="text-sm text-gray-600 text-center">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-600 font-medium hover:underline">Sign in</Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default Signup;